import { useState, useEffect, useCallback, useRef } from "react";
import React from "react";
import { Plus, Search, X, Trash2, Pencil, Users, LogOut, Crown } from "lucide-react";
import GroupDetails from "./GroupDetails";
import axiosInstance from "../../API/interceptor";

// ── Constants ─────────────────────────────────────────────────────────────────
export const AV_COLORS = ["#7C3AED","#0369A1","#BE185D","#047857","#D97706","#DC2626","#0891B2","#059669","#6D28D9","#0D9488","#DB2777"];

/* Map API response group → component shape */
function mapGroup(grp) {
  return {
    id:            grp._id,
    name:          grp.group_name,
    role:          grp.is_admin ? "admin" : "member",
    members:       (grp.member_details || []).length,
    memberList:    (grp.member_details || []).map(m => ({ id: m.id, name: m.name, canWrite: m.can_write })),
    color:         AV_COLORS[(grp.group_name?.charCodeAt(0) || 0) % AV_COLORS.length],
    is_admin:      grp.is_admin,
    canWrite:      grp.current_user_can_write,
    admin_id:      grp.admin_id,
    raw:           grp,
  };
}

// ── shared style fragments ────────────────────────────────────────────────────
const HEADER_GRADIENT = "bg-[linear-gradient(135deg,#7C3AED_0%,#6D28D9_60%,#4F46E5_100%)]";
const FOOTER_BTN_GRADIENT = "bg-[linear-gradient(135deg,#7C3AED,#6D28D9)] shadow-[0_4px_14px_rgba(124,58,237,.35)]";
const INPUT_BASE = "border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none bg-white w-full transition-all shadow-[0_1px_4px_rgba(108,95,252,0.06)] focus:border-violet-600 focus:shadow-[0_0_0_3px_rgba(124,58,237,0.1)]";

// ── CreateGroupModal ──────────────────────────────────────────────────────────

function CreateGroupModal({ onClose, onCreated }) {
  const [name,          setName]          = useState("");
  const [memberSearch,  setMemberSearch]  = useState("");
  const [memberOpen,    setMemberOpen]    = useState(false);
  const [searchResults, setSearchResults] = useState([]);  // live API results
  const [searching,     setSearching]     = useState(false);
  const [members,       setMembers]       = useState([]);  // { id, name }
  const [file,          setFile]          = useState(null);
  const [saving,        setSaving]        = useState(false);
  const memberRef = React.useRef();
  const debRef    = useRef(null);
  const abortRef  = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debRef.current);
    abortRef.current?.abort();
    debRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setSearching(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        setSearchResults(arr.map(u => ({ id: String(u.staffid), name: u.display_name || u.alias_name || String(u.staffid) })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setSearchResults([]);
      } finally { setSearching(false); }
    }, 300);
  }, []);

  /* Trigger search whenever input changes or dropdown opens */
  useEffect(() => {
    if (memberOpen) doSearch(memberSearch);
  }, [memberSearch, memberOpen, doSearch]);

  useEffect(() => {
    const fn = e => { if (memberRef.current && !memberRef.current.contains(e.target)) setMemberOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const toggleMember = m => setMembers(prev =>
    prev.find(x => x.id === m.id) ? prev.filter(x => x.id !== m.id) : [...prev, m]
  );

  // Exclude already-selected members from results
  const filtered = searchResults.filter(u => !members.find(m => m.id === u.id));

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("group_name", name.trim());
      members.forEach(m => fd.append("members", m.id));
      if (file) fd.append("group_file", file);
      const res = await axiosInstance.post("/task_assignment/todo_group/create", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.success) { onCreated(); onClose(); }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f0a28]/55 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className={`relative flex items-center justify-between px-6 py-5 overflow-hidden ${HEADER_GRADIENT}`}>
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-20 bg-white/30" />
          <div className="relative z-10">
            <h2 className="text-base font-bold text-white m-0 leading-tight">Create New Group</h2>
            <p className="text-[11px] text-violet-200 m-0 mt-0.5">Add a new group to organize tasks</p>
          </div>
          <button onClick={onClose} className="relative z-10 w-8 h-8 rounded-lg flex items-center justify-center border-none cursor-pointer bg-white/10 hover:bg-white/20">
            <X size={16} color="#fff" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-5 bg-[#fafbff]">
          {/* Group Name */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Group Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Design Team..."
              className={INPUT_BASE}
              onKeyDown={e => { if (e.key === "Enter" && name.trim()) handleCreate(); }}
            />
          </div>

          {/* Add Members */}
          <div ref={memberRef}>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Add Members</label>
            <div
              className={`border rounded-xl px-3 py-2 bg-white cursor-pointer transition-all min-h-[46px] flex flex-wrap items-center gap-1.5 ${
                memberOpen
                  ? "border-violet-600 shadow-[0_0_0_3px_rgba(124,58,237,0.1)]"
                  : "border-gray-200 shadow-[0_1px_4px_rgba(108,95,252,0.06)]"
              }`}
              onClick={() => setMemberOpen(o => !o)}>
              {members.length === 0 && <span className="text-sm text-slate-400 flex-1">Select members...</span>}
              {members.map(m => (
                <span key={m.id} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full text-white"
                  style={{ background: AV_COLORS[(m.name?.charCodeAt(0) || 0) % AV_COLORS.length] }}>
                  {m.name}
                  <span onClick={e => { e.stopPropagation(); toggleMember(m); }} className="cursor-pointer opacity-70 hover:opacity-100 leading-none">×</span>
                </span>
              ))}
            </div>
            {memberOpen && (
              <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[100]">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                  <Search size={12} className="text-slate-400 flex-shrink-0" />
                  <input autoFocus value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                    placeholder="Search members..."
                    className="border-none outline-none text-xs text-slate-700 w-full bg-transparent placeholder:text-slate-400"
                    onClick={e => e.stopPropagation()} />
                </div>
                <div className="max-h-44 overflow-y-auto">
                  {searching && (
                    <p className="text-center text-xs text-slate-400 py-4">Searching...</p>
                  )}
                  {!searching && filtered.map((m, i) => {
                    const avatarColor = AV_COLORS[(m.name?.charCodeAt(0) || 0) % AV_COLORS.length];
                    return (
                      <div key={m.id} onClick={e => { e.stopPropagation(); toggleMember(m); }}
                        className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors hover:bg-slate-50 ${i < filtered.length - 1 ? "border-b border-slate-50" : ""}`}>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                          style={{ background: avatarColor }}>{m.name.charAt(0).toUpperCase()}</div>
                        <span className="text-xs font-medium text-slate-700">{m.name}</span>
                      </div>
                    );
                  })}
                  {!searching && filtered.length === 0 && (
                    <p className="text-center text-xs text-slate-400 py-4">No results</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* CSV/XLSX file */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Import File (CSV/XLSX)</label>
            <input type="file" accept=".csv,.xlsx,.xls"
              onChange={e => setFile(e.target.files[0] || null)}
              className="w-full border border-dashed border-gray-300 rounded-xl px-4 py-3 text-sm text-slate-500 cursor-pointer bg-gray-50" />
            {file && <p className="text-xs text-green-600 mt-1 font-semibold">✅ {file.name}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-600 bg-white cursor-pointer hover:bg-slate-50">Cancel</button>
          <button onClick={handleCreate} disabled={!name.trim() || saving}
            className={`px-5 py-2.5 rounded-xl border-none text-white text-sm font-semibold cursor-pointer hover:opacity-90 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${FOOTER_BTN_GRADIENT}`}>
            {saving ? "Creating..." : "Create Group"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── EditGroupModal ────────────────────────────────────────────────────────────

function EditGroupModal({ group, onClose, onSaved }) {
  const [name,          setName]          = useState(group.name);
  const [memberSearch,  setMemberSearch]  = useState("");
  const [memberOpen,    setMemberOpen]    = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [members,       setMembers]       = useState(group.memberList || []);
  const [saving,        setSaving]        = useState(false);
  const memberRef = React.useRef();
  const debRef    = useRef(null);
  const abortRef  = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debRef.current);
    abortRef.current?.abort();
    debRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setSearching(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        setSearchResults(arr.map(u => ({ id: String(u.staffid), name: u.display_name || u.alias_name || String(u.staffid) })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setSearchResults([]);
      } finally { setSearching(false); }
    }, 300);
  }, []);

  useEffect(() => {
    if (memberOpen) doSearch(memberSearch);
  }, [memberSearch, memberOpen, doSearch]);

  useEffect(() => {
    const fn = e => { if (memberRef.current && !memberRef.current.contains(e.target)) setMemberOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const toggleMember = m => setMembers(prev =>
    prev.find(x => String(x.id) === String(m.id)) ? prev.filter(x => String(x.id) !== String(m.id)) : [...prev, m]
  );
  const filtered = searchResults.filter(u => !members.find(m => String(m.id) === String(u.id)));

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("group_name", name.trim());
      members.forEach(m => fd.append("members", m.id));
      const res = await axiosInstance.post(`/task_assignment/todo_group/update/${group.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.success) { onSaved(); onClose(); }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f0a28]/55 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className={`relative flex items-center justify-between px-6 py-5 overflow-hidden ${HEADER_GRADIENT}`}>
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-20 bg-white/30" />
          <div className="relative z-10">
            <h2 className="text-base font-bold text-white m-0 leading-tight">Edit Group</h2>
            <p className="text-[11px] text-violet-200 m-0 mt-0.5">Update group details</p>
          </div>
          <button onClick={onClose} className="relative z-10 w-8 h-8 rounded-lg flex items-center justify-center border-none cursor-pointer bg-white/10 hover:bg-white/20">
            <X size={16} color="#fff" />
          </button>
        </div>
        <div className="px-6 py-5 flex flex-col gap-5 bg-[#fafbff]">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Group Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className={INPUT_BASE} />
          </div>
          <div ref={memberRef}>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-2">Members</label>
            <div
              className={`border rounded-xl px-3 py-2 bg-white cursor-pointer min-h-[46px] flex flex-wrap items-center gap-1.5 ${
                memberOpen ? "border-violet-600" : "border-gray-200"
              }`}
              onClick={() => setMemberOpen(o => !o)}>
              {members.length === 0 && <span className="text-sm text-slate-400 flex-1">Select members...</span>}
              {members.map(m => (
                <span key={m.id} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full text-white"
                  style={{ background: AV_COLORS[(m.name?.charCodeAt(0) || 0) % AV_COLORS.length] }}>
                  {m.name}
                  <span onClick={e => { e.stopPropagation(); toggleMember(m); }} className="cursor-pointer opacity-70 hover:opacity-100 leading-none">×</span>
                </span>
              ))}
            </div>
            {memberOpen && (
              <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[100]">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                  <Search size={12} className="text-slate-400 flex-shrink-0" />
                  <input autoFocus value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                    placeholder="Search members..."
                    className="border-none outline-none text-xs text-slate-700 w-full bg-transparent placeholder:text-slate-400"
                    onClick={e => e.stopPropagation()} />
                </div>
                <div className="max-h-44 overflow-y-auto">
                  {searching && (
                    <p className="text-center text-xs text-slate-400 py-4">Searching...</p>
                  )}
                  {!searching && filtered.map((m, i) => {
                    const avatarColor = AV_COLORS[(m.name?.charCodeAt(0) || 0) % AV_COLORS.length];
                    return (
                      <div key={m.id} onClick={e => { e.stopPropagation(); toggleMember(m); }}
                        className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors hover:bg-slate-50 ${i < filtered.length - 1 ? "border-b border-slate-50" : ""}`}>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                          style={{ background: avatarColor }}>{m.name.charAt(0).toUpperCase()}</div>
                        <span className="text-xs font-medium text-slate-700">{m.name}</span>
                      </div>
                    );
                  })}
                  {!searching && filtered.length === 0 && (
                    <p className="text-center text-xs text-slate-400 py-4">No results</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-600 bg-white cursor-pointer hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={!name.trim() || saving}
            className={`px-5 py-2.5 rounded-xl border-none text-white text-sm font-semibold cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed ${FOOTER_BTN_GRADIENT}`}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DeleteGroupModal ──────────────────────────────────────────────────────────

function DeleteGroupModal({ group, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f0a28]/55 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex flex-col items-center px-6 pt-8 pb-5 gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-red-50 border-2 border-red-200">
            <Trash2 size={28} color="#ef4444" />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-bold text-slate-800 m-0">Delete Group</h2>
            <p className="text-sm text-slate-500 mt-2 m-0">
              <span className="font-semibold text-slate-700">&ldquo;{group.name}&rdquo;</span> will be permanently deleted along with all its rows.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-600 bg-white cursor-pointer hover:bg-slate-50">Cancel</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2.5 rounded-xl border-none text-white text-sm font-semibold cursor-pointer hover:opacity-90 active:scale-95 bg-[linear-gradient(135deg,#ef4444,#dc2626)] shadow-[0_4px_14px_rgba(239,68,68,.35)]">
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MembersModal ──────────────────────────────────────────────────────────────

function MembersModal({ groupId, groupName, permData, onClose, onReload, onActiveChanged }) {
  const [members,         setMembers]         = useState(permData.member_details || []);
  const [confirmTransfer, setConfirmTransfer] = useState(null); // { id, name }
  const [confirmLeave,    setConfirmLeave]    = useState(false);
  const [toggling,        setToggling]        = useState({});   // { [memberId]: bool }
  const [busy,            setBusy]            = useState(false);

  const isAdmin = permData.current_user_is_admin;
  const adminId = String(permData.admin_id || "");

  const toggleWrite = async (m, val) => {
    setToggling(p => ({ ...p, [m.id]: true }));
    try {
      const r = await axiosInstance.post("/task_assignment/todo_group/permission/update",
        { group_id: groupId, member_id: m.id, write: val ? "1" : "0" });
      if (r.data?.success)
        setMembers(prev => prev.map(x => x.id === m.id ? { ...x, can_write: val } : x));
    } catch { /* silent */ }
    setToggling(p => ({ ...p, [m.id]: false }));
  };

  const doTransfer = async () => {
    if (!confirmTransfer) return;
    setBusy(true);
    try {
      const r = await axiosInstance.post("/task_assignment/todo_group/transfer_admin",
        { group_id: groupId, new_admin_id: confirmTransfer.id });
      if (r.data?.success) {
        onClose();
        onReload();
        if (onActiveChanged) onActiveChanged(groupId);
      }
    } catch { /* silent */ }
    setBusy(false);
  };

  const doLeave = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post("/task_assignment/todo_group/leave", { group_id: groupId });
      if (r.data?.success) {
        onClose();
        onReload();
        if (onActiveChanged) onActiveChanged(groupId);
      }
    } catch { /* silent */ }
    setBusy(false);
  };

  const OVERLAY = "fixed inset-0 z-[9999] flex items-center justify-center bg-[#0f0a28]/55 backdrop-blur-sm";

  // ── Transfer admin confirmation ──
  if (confirmTransfer) {
    return (
      <div className={OVERLAY}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="flex flex-col items-center px-6 pt-8 pb-5 gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-amber-50 border-2 border-amber-200">
              <Crown size={26} color="#d97706" />
            </div>
            <div className="text-center">
              <h2 className="text-base font-bold text-slate-800 m-0">Transfer Admin Role?</h2>
              <p className="text-sm text-slate-500 mt-2 m-0">
                <span className="font-semibold text-slate-700">{confirmTransfer.name}</span> will become the new admin.
                <br /><span className="text-xs text-slate-400">You will become a regular member.</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-6 pb-6">
            <button onClick={() => setConfirmTransfer(null)} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-600 bg-white cursor-pointer hover:bg-slate-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={doTransfer} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-xl border-none text-white text-sm font-semibold cursor-pointer disabled:opacity-50 bg-[linear-gradient(135deg,#f59e0b,#d97706)] shadow-[0_4px_14px_rgba(245,158,11,.35)]">
              {busy ? "Transferring..." : "Yes, Transfer"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Leave group confirmation ──
  if (confirmLeave) {
    return (
      <div className={OVERLAY}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="flex flex-col items-center px-6 pt-8 pb-5 gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-red-50 border-2 border-red-200">
              <LogOut size={26} color="#ef4444" />
            </div>
            <div className="text-center">
              <h2 className="text-base font-bold text-slate-800 m-0">Leave Group?</h2>
              <p className="text-sm text-slate-500 mt-2 m-0">
                Are you sure you want to leave <span className="font-semibold text-slate-700">&ldquo;{groupName}&rdquo;</span>?
                <br /><span className="text-xs text-slate-400">Your data will remain in the group.</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-6 pb-6">
            <button onClick={() => setConfirmLeave(false)} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-600 bg-white cursor-pointer hover:bg-slate-50 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={doLeave} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-xl border-none text-white text-sm font-semibold cursor-pointer disabled:opacity-50 bg-[linear-gradient(135deg,#ef4444,#dc2626)] shadow-[0_4px_14px_rgba(239,68,68,.35)]">
              {busy ? "Leaving..." : "Yes, Leave"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main modal ──
  return (
    <div className={OVERLAY} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0 bg-[linear-gradient(135deg,#0369A1,#0284C7)]">
          <span className="text-white font-bold text-sm flex items-center gap-2">
            <Users size={14} /> Members &amp; Permissions
          </span>
          <button onClick={onClose}
            className="bg-white/10 border-none rounded-lg w-8 h-8 flex items-center justify-center cursor-pointer text-white hover:bg-white/20">
            <X size={14} />
          </button>
        </div>

        {/* Admin info banner */}
        {isAdmin && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-xs text-blue-800 flex-shrink-0">
            <strong>Admin Controls:</strong> Use the write toggle to grant edit access. Use &ldquo;Make Admin&rdquo; to transfer the admin role.
          </div>
        )}

        {/* Members list */}
        <div className="overflow-y-auto flex-1 p-4 flex flex-col gap-2">
          {members.map((m, i) => {
            const mid      = String(m.id);
            const isMeAdm  = mid === adminId;
            return (
              <div key={m.id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                  isMeAdm ? "bg-violet-50 border-violet-100" : "bg-slate-50 border-slate-100"
                }`}>
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ background: AV_COLORS[i % AV_COLORS.length] }}>
                  {(m.name || "?").charAt(0).toUpperCase()}
                </div>
                {/* Name + badge */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-700 m-0 truncate">{m.name}</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md mt-0.5 ${
                    isMeAdm
                      ? "bg-violet-100 text-violet-600"
                      : m.can_write
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-800"
                  }`}>
                    {isMeAdm ? "👑 Admin" : m.can_write ? "✏️ Write" : "🔒 Read Only"}
                  </span>
                </div>
                {/* Write toggle — admin only, not for admin member */}
                {isAdmin && !isMeAdm && (
                  <label className={`flex items-center gap-[5px] ${toggling[m.id] ? "cursor-not-allowed" : "cursor-pointer"}`}
                    title={m.can_write ? "Revoke write access" : "Grant write access"}>
                    <span className="text-[10px] text-gray-500 font-semibold whitespace-nowrap">Write</span>
                    <div className="relative w-9 h-5 flex-shrink-0"
                      onClick={() => { if (!toggling[m.id]) toggleWrite(m, !m.can_write); }}>
                      <div className={`absolute inset-0 rounded-full transition-colors ${
                        m.can_write ? "bg-green-600" : "bg-gray-300"
                      } ${toggling[m.id] ? "opacity-50" : "opacity-100"}`}>
                        <div className={`absolute top-[3px] w-3.5 h-3.5 rounded-full bg-white transition-all shadow-[0_1px_3px_rgba(0,0,0,0.2)] ${
                          m.can_write ? "left-[19px]" : "left-[3px]"
                        }`} />
                      </div>
                    </div>
                  </label>
                )}
                {/* Make Admin button — admin only, not for admin member */}
                {isAdmin && !isMeAdm && (
                  <button onClick={() => setConfirmTransfer({ id: m.id, name: m.name })}
                    className="px-2 py-[3px] border border-amber-200 rounded-md text-[10px] font-bold cursor-pointer bg-amber-50 text-amber-800 whitespace-nowrap">
                    👑 Make Admin
                  </button>
                )}
              </div>
            );
          })}
          {members.length === 0 && (
            <p className="text-center text-xs text-slate-400 py-6">No members found</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-white flex-shrink-0">
          {!isAdmin ? (
            <button onClick={() => setConfirmLeave(true)}
              className="px-3.5 py-1.5 border border-red-200 rounded-lg bg-white text-red-500 text-xs font-semibold cursor-pointer flex items-center gap-[5px]">
              <LogOut size={12} /> Leave Group
            </button>
          ) : (
            <span className="text-[11px] text-gray-400">Transfer admin role before leaving.</span>
          )}
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-xl border-none text-white text-sm font-semibold cursor-pointer hover:opacity-90 bg-[linear-gradient(135deg,#0369A1,#0284C7)]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── HoverIconBtn ──────────────────────────────────────────────────────────────

function HoverIconBtn({ icon, label, color, bg, border, onClick }) {
  const [tipPos, setTipPos] = React.useState(null);
  const btnRef = React.useRef();
  return (
    <button ref={btnRef} onClick={onClick}
      onMouseEnter={() => {
        if (btnRef.current) {
          const r = btnRef.current.getBoundingClientRect();
          setTipPos({ top: r.top - 28, left: r.left + r.width / 2 });
        }
      }}
      onMouseLeave={() => setTipPos(null)}
      className="w-[22px] h-[22px] flex items-center justify-center rounded-md border cursor-pointer flex-shrink-0 relative"
      style={{ borderColor: border, background: bg, color }}>
      {icon}
      {tipPos && (
        <div className="fixed whitespace-nowrap text-[10px] font-semibold text-white bg-[#1e1b4b] px-2 py-[3px] rounded-md pointer-events-none z-[99999]"
          style={{ top: tipPos.top, left: tipPos.left, transform: "translateX(-50%)" }}>
          {label}
        </div>
      )}
    </button>
  );
}

// ── GroupItem ─────────────────────────────────────────────────────────────────

function GroupItem({ g, isActive, onSelect, onDelete, onEdit, onPerm }) {
  const isAdmin = g.is_admin;
  return (
    <div className="group relative flex items-center box-border w-full">
      <button onClick={onSelect}
        className={`w-full flex items-center gap-2.5 px-4 py-2.5 border-none text-left box-border transition-colors border-r-2 ${
          isActive ? "border-violet-600 bg-violet-50" : "border-transparent hover:bg-slate-50"
        }`}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ background: g.color }}>
          {g.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <p className={`text-xs font-semibold m-0 overflow-hidden text-ellipsis whitespace-nowrap ${isActive ? "text-violet-600" : "text-slate-700"}`}>{g.name}</p>
          <p className="text-[10px] text-slate-400 m-0">{g.members} members</p>
        </div>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 border transition-opacity group-hover:opacity-0 ${
          isAdmin ? "bg-violet-100 text-violet-600 border-violet-200" : "bg-slate-100 text-slate-500 border-slate-200"
        }`}>
          {isAdmin ? "Admin" : "Member"}
        </span>
      </button>
      <div className="absolute right-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity">
        {isAdmin && <HoverIconBtn icon={<Pencil size={11}/>} label="Edit" color="#7C3AED" bg="#f5f3ff" border="#ede9fe" onClick={e => { e.stopPropagation(); onEdit(); }} />}
        {isAdmin && <HoverIconBtn icon={<Trash2 size={11}/>} label="Delete" color="#ef4444" bg="#fef2f2" border="#fecaca" onClick={e => { e.stopPropagation(); onDelete(); }} />}
        <HoverIconBtn icon={<Users size={11}/>} label="Members" color="#0369A1" bg="#eff6ff" border="#bfdbfe" onClick={e => { e.stopPropagation(); onPerm(); }} />
      </div>
    </div>
  );
}

// ── GroupTab (default export) ─────────────────────────────────────────────────

export default function GroupTab() {
  const [groups,       setGroups]       = useState([]);
  const [activeId,     setActiveId]     = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editGroup,    setEditGroup]    = useState(null);
  const [deleteGroup,  setDeleteGroup]  = useState(null);
  const [permGroupId,   setPermGroupId]   = useState(null);
  const [permGroupName, setPermGroupName] = useState("");
  const [permData,      setPermData]      = useState(null);

  /* ════ LOAD GROUPS — GET /task_assignment/todo_group/list ════ */
  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/task_assignment/todo_group/list");
      if (res.data?.success && Array.isArray(res.data.groups)) {
        setGroups(res.data.groups.map(mapGroup));
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  /* ── Delete group ── */
  const handleDeleteConfirm = async () => {
    try {
      await axiosInstance.post(`/task_assignment/todo_group/delete/${deleteGroup.id}`);
      if (activeId === deleteGroup.id) setActiveId(null);
      loadGroups();
    } catch { /* silent */ }
    setDeleteGroup(null);
  };

  /* ── Load members/perms for perm modal ── */
  const openPermModal = async (groupId) => {
    const grp = groups.find(g => g.id === groupId);
    try {
      const res = await axiosInstance.get(`/task_assignment/todo_group/tasks/${groupId}`);
      if (res.data?.success) {
        setPermData(res.data);
        setPermGroupId(groupId);
        setPermGroupName(grp?.name || "");
      }
    } catch { /* silent */ }
  };

  const closePermModal = () => { setPermGroupId(null); setPermGroupName(""); setPermData(null); };

  /* ── Called after admin transfer or leave — clear active if it was this group ── */
  const handleGroupActiveChanged = (gid) => {
    if (activeId === gid) setActiveId(null);
  };

  const activeGroupData = groups.find(g => g.id === activeId) ?? null;

  return (
    <div className="flex h-[calc(100vh-110px)] min-h-[500px]">
      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={loadGroups} />}
      {editGroup  && <EditGroupModal   group={editGroup}   onClose={() => setEditGroup(null)}  onSaved={loadGroups} />}
      {deleteGroup && <DeleteGroupModal group={deleteGroup} onClose={() => setDeleteGroup(null)} onConfirm={handleDeleteConfirm} />}

      {permGroupId && permData && (
        <MembersModal
          groupId={permGroupId}
          groupName={permGroupName}
          permData={permData}
          onClose={closePermModal}
          onReload={loadGroups}
          onActiveChanged={handleGroupActiveChanged}
        />
      )}

      {/* ── Left Sidebar ── */}
      <div className="w-64 flex-shrink-0 bg-white border-r border-gray-100 flex flex-col overflow-hidden min-h-0">
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[linear-gradient(135deg,#7C3AED,#6D28D9)]">
              <Users size={13} color="#fff" />
            </div>
            <span className="text-sm font-extrabold text-slate-800">My Circles</span>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border border-violet-200 hover:bg-violet-50 transition-colors bg-violet-50">
            <Plus size={14} color="#7C3AED" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
          {loading && (
            <div className="py-8 text-center">
              <div className="w-6 h-6 border-2 border-violet-100 border-t-violet-600 rounded-full animate-spin mx-auto" />
            </div>
          )}
          {!loading && groups.length === 0 && (
            <p className="text-center text-xs text-slate-400 py-8">No groups yet</p>
          )}
          {groups.map(g => (
            <GroupItem key={g.id} g={g} isActive={activeId === g.id}
              onSelect={() => setActiveId(g.id)}
              onDelete={() => setDeleteGroup(g)}
              onEdit={() => setEditGroup(g)}
              onPerm={() => openPermModal(g.id)}
            />
          ))}
        </div>
      </div>

      {/* ── Right Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {activeGroupData ? (
          <GroupDetails group={activeGroupData} onReload={loadGroups} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-violet-50">
              <Users size={26} color="#7C3AED" />
            </div>
            <p className="text-sm font-semibold text-slate-500 text-center m-0">Select a group from the left<br/>or create a new one</p>
          </div>
        )}
      </div>
    </div>
  );
}